import type { AiModelEnum, AttachmentDetails, EvidenceSessionDetailsAverageFeatures } from 'api';
import { useState } from 'react';
import {
  EvidenceSessionPhotoGallery,
  type EvidenceSessionPhotoGalleryItem,
  Grid,
  MediaDialog,
  Stack,
  Typography,
  useBreakpoint,
} from 'ui';

import { MaterialAiBreakdown } from '../../../breakdowns';
import { AnnotatedMediaItem, useAnnotations } from '../../../quality-report';
import { IGNORE_BALE_LABELS } from '../../../quality-report/constants';
import { AiFilters } from '../AiFilters/AiFilters';
import { EvidenceSessionStats } from '../EvidenceSessionStats/EvidenceSessionStats';

export const EvidenceAiDetails = ({
  averageFeatures,
  photoEvidence,
  stats,
  model,
  attachmentDetails,
  targetWeightPercentage,
}: {
  averageFeatures: EvidenceSessionDetailsAverageFeatures;
  photoEvidence: EvidenceSessionPhotoGalleryItem[];
  stats: {
    failed: number;
    passed: number;
    total: number;
  };
  model: AiModelEnum;
  attachmentDetails?: AttachmentDetails | null;
  targetWeightPercentage?: number | null;
}) => {
  const { isMobile } = useBreakpoint();
  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
    showSidebar,
  } = useAnnotations({});

  const carouselMedia = photoEvidence.map((file) => file.fullResolutionSrc);

  const [selectedImage, setSelectedImage] = useState<string>('');

  const handleSelect = (src: string) => {
    const relatedMedia = photoEvidence.find((file) => file.src === src || file.fullResolutionSrc === src);
    setSelectedImage(relatedMedia?.fullResolutionSrc || '');
    handleImageChange(relatedMedia?.annotations);
  };

  const showSwitch = Boolean(selectedAnnotation?.some(({ label }) => !IGNORE_BALE_LABELS.includes(label)));
  return (
    <>
      <Grid container spacing={3} data-testid="evidence-session-details">
        <Grid item xs={12} md={6} data-testid="photo-evidence">
          <Stack gap={2}>
            <Typography variant="h6" fontWeight="bold">
              AI images
            </Typography>
            {photoEvidence.length ? (
              <>
                <EvidenceSessionStats {...stats} />
                <EvidenceSessionPhotoGallery
                  photoEvidence={photoEvidence}
                  maxImages={isMobile ? 6 : 8}
                  handleClick={(photo) => handleSelect(photo.src)}
                />
              </>
            ) : (
              <Typography>No data</Typography>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} data-testid="composition-report">
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            AI visual analysis
          </Typography>
          {model === 'pet' ? (
            <MaterialAiBreakdown materials={averageFeatures} variant={model} />
          ) : attachmentDetails && targetWeightPercentage ? (
            <MaterialAiBreakdown
              variant={model}
              attachmentDetails={attachmentDetails}
              targetWeightPercentage={targetWeightPercentage}
            />
          ) : (
            <Typography>No data</Typography>
          )}
        </Grid>
      </Grid>

      <MediaDialog
        open={Boolean(selectedImage)}
        selectedMedium={selectedImage}
        onClose={() => setSelectedImage('')}
        media={carouselMedia}
        handleNavigate={handleSelect}
        slotProps={{
          imageCarousel: {
            slotProps: {
              ...(selectedAnnotation && model
                ? {
                    mediaItemSlot: (
                      <AnnotatedMediaItem
                        image={selectedImage}
                        annotations={selectedAnnotation}
                        hiddenCategories={hiddenCategories}
                        showLegend={showLegend}
                        model={model}
                      />
                    ),
                    sidebar: showSidebar
                      ? {
                          children: (
                            <AiFilters
                              selectedAnnotation={selectedAnnotation}
                              hiddenCategories={hiddenCategories}
                              handleHideCategory={handleHideCategory}
                              handleToggleLegend={handleToggleLegend}
                              showLegend={showLegend}
                              showSwitch={showSwitch}
                              model={model}
                              showAerosols
                            />
                          ),
                          label: 'Adjust AI Filters',
                        }
                      : undefined,
                  }
                : null),
            },
          },
        }}
      />
    </>
  );
};
